import { useCallback, useMemo, useRef, useState } from "react";
import { requestAccountLinkingScreencastToken } from "modules/checkout/services/accountLinkingRemoteBrowser";
import { AccountLinkingRemoteBrowserPayload } from "modules/checkout/types/accountLinkingRemoteBrowser";
import { StoreSessionStatus } from "modules/checkout/types/optimizedCart";

interface UseAccountLinkingRemoteBrowserParams {
  stores: StoreSessionStatus[];
}

interface AccountLinkingRemoteBrowserState {
  activeStoreId: number | null;
  error: string | null;
  isBrowserOpen: boolean;
  isConfirming: boolean;
  isOpening: boolean;
  localConnectedStoreIds: number[];
  pendingStores: StoreSessionStatus[];
  nextPendingStore: StoreSessionStatus | null;
  primaryCtaLabel: "Comenzar conexión" | "Conectar Siguiente" | "Crear Carritos";
  canCreateCarts: boolean;
  remoteBrowserPayload: AccountLinkingRemoteBrowserPayload | null;
  closeBrowser: () => void;
  confirmActiveStoreConnected: () => Promise<boolean>;
  refreshActiveStoreToken: (storeId: number) => Promise<string>;
  startLinking: (storeId: number) => Promise<boolean>;
  startNextPendingStore: () => Promise<boolean>;
}

const TOKEN_REQUEST_ERROR = "No pudimos abrir la conexión remota. Inténtalo nuevamente.";

const hasStoreId = (stores: StoreSessionStatus[], storeId: number): boolean => {
  return stores.some((store) => store.store_id === storeId);
};

/**
 * Owns checkout account-linking state before RemoteBrowser UI integration.
 * @param params Disconnected or pending store sessions that block cart creation.
 * @returns State derivations and callbacks for token request, local confirmation, and duplicate guards.
 */
export const useAccountLinkingRemoteBrowser = ({
  stores,
}: UseAccountLinkingRemoteBrowserParams): AccountLinkingRemoteBrowserState => {
  const [activeStoreId, setActiveStoreId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isBrowserOpen, setIsBrowserOpen] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isOpening, setIsOpening] = useState(false);
  const [localConnectedStoreIds, setLocalConnectedStoreIds] = useState<number[]>([]);
  const [remoteBrowserPayload, setRemoteBrowserPayload] = useState<AccountLinkingRemoteBrowserPayload | null>(null);
  const openingRef = useRef(false);
  const confirmingRef = useRef(false);

  const pendingStores = useMemo(() => {
    return stores.filter((store) => !store.connected && !localConnectedStoreIds.includes(store.store_id));
  }, [localConnectedStoreIds, stores]);

  const nextPendingStore = pendingStores[0] ?? null;
  const canCreateCarts = pendingStores.length === 0;
  const primaryCtaLabel = canCreateCarts
    ? "Crear Carritos"
    : localConnectedStoreIds.length > 0
      ? "Conectar Siguiente"
      : "Comenzar conexión";

  const closeBrowser = useCallback(() => {
    setIsBrowserOpen(false);
    setActiveStoreId(null);
    setRemoteBrowserPayload(null);
  }, []);

  const startLinking = useCallback(async (storeId: number): Promise<boolean> => {
    if (
      openingRef.current ||
      confirmingRef.current ||
      isBrowserOpen ||
      localConnectedStoreIds.includes(storeId) ||
      !hasStoreId(stores, storeId)
    ) {
      return false;
    }

    openingRef.current = true;
    setIsOpening(true);
    setError(null);

    try {
      const tokenPayload = await requestAccountLinkingScreencastToken({ storeId });
      setActiveStoreId(storeId);
      setRemoteBrowserPayload({ storeId, token: tokenPayload.token });
      setIsBrowserOpen(true);
      return true;
    } catch {
      setError(TOKEN_REQUEST_ERROR);
      setIsBrowserOpen(false);
      setRemoteBrowserPayload(null);
      setActiveStoreId(null);
      return false;
    } finally {
      openingRef.current = false;
      setIsOpening(false);
    }
  }, [isBrowserOpen, localConnectedStoreIds, stores]);

  const startNextPendingStore = useCallback(async (): Promise<boolean> => {
    if (!nextPendingStore) {
      return false;
    }

    return await startLinking(nextPendingStore.store_id);
  }, [nextPendingStore, startLinking]);

  const confirmActiveStoreConnected = useCallback(async (): Promise<boolean> => {
    if (confirmingRef.current || openingRef.current || activeStoreId === null) {
      return false;
    }

    confirmingRef.current = true;
    setIsConfirming(true);

    try {
      setLocalConnectedStoreIds((currentStoreIds) => {
        if (currentStoreIds.includes(activeStoreId)) {
          return currentStoreIds;
        }

        return [...currentStoreIds, activeStoreId];
      });
      closeBrowser();
      return true;
    } finally {
      confirmingRef.current = false;
      setIsConfirming(false);
    }
  }, [activeStoreId, closeBrowser]);

  const refreshActiveStoreToken = useCallback(async (storeId: number): Promise<string> => {
    const tokenPayload = await requestAccountLinkingScreencastToken({ storeId });
    setRemoteBrowserPayload({ storeId, token: tokenPayload.token });
    setError(null);
    return tokenPayload.token;
  }, []);

  return {
    activeStoreId,
    error,
    isBrowserOpen,
    isConfirming,
    isOpening,
    localConnectedStoreIds,
    pendingStores,
    nextPendingStore,
    primaryCtaLabel,
    canCreateCarts,
    remoteBrowserPayload,
    closeBrowser,
    confirmActiveStoreConnected,
    refreshActiveStoreToken,
    startLinking,
    startNextPendingStore,
  };
};
